import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { Building2, Users, FileText, LogIn, Search, Edit, Eye, Trash2, Plus, Settings, Palette, Save, RefreshCw, UserPlus, Mail, Key, ChevronDown, User, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SuperAdminDashboard = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('workspaces');
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaceStats, setWorkspaceStats] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    subdomain: '',
    logo_url: '',
    primary_color: '#7c3aed',
    secondary_color: '#14b8a6',
    welcome_message: '',
    contact_email: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Create workspace state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    subdomain: '',
    admin_email: '',
    admin_name: '',
    admin_password: ''
  });
  const [creating, setCreating] = useState(false);

  // System settings state
  const [systemSettings, setSystemSettings] = useState({
    logo_url: '',
    app_name: 'DocuBot',
    primary_color: '#7c3aed',
    secondary_color: '#14b8a6',
    sidebar_color: '#18181b',
    favicon_url: '',
    login_background_url: '',
    theme: 'light'
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Member management state
  const [selectedWorkspaceForMembers, setSelectedWorkspaceForMembers] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [savingMember, setSavingMember] = useState(false);

  useEffect(() => {
    if (user.is_super_admin) {
      fetchWorkspaces();
      loadSystemSettings();
    }
  }, [user]);

  const loadSystemSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/system/settings`);
      setSystemSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  const saveSystemSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/system/settings`, systemSettings);
      toast.success('System settings saved! Refresh to see changes.');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Member management functions
  const fetchWorkspaceMembers = async (workspace) => {
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${API_URL}/admin/workspaces/${workspace.id}/members`);
      setWorkspaceMembers(res.data.members || []);
    } catch (error) {
      toast.error('Failed to fetch workspace members');
      setWorkspaceMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleManageMembers = (workspace) => {
    setSelectedWorkspaceForMembers(workspace);
    setMemberDialogOpen(true);
    fetchWorkspaceMembers(workspace);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setEditMemberForm({
      name: member.name || '',
      email: member.email || '',
      password: '',
      role: member.role || 'viewer'
    });
    setEditMemberDialogOpen(true);
  };

  const handleSaveMember = async () => {
    if (!selectedMember || !selectedWorkspaceForMembers) return;
    
    setSavingMember(true);
    try {
      const updateData = {
        name: editMemberForm.name,
        email: editMemberForm.email,
        role: editMemberForm.role
      };
      
      if (editMemberForm.password) {
        updateData.password = editMemberForm.password;
      }
      
      await axios.put(
        `${API_URL}/admin/workspaces/${selectedWorkspaceForMembers.id}/members/${selectedMember.id}`,
        updateData
      );
      
      toast.success('Member updated successfully');
      setEditMemberDialogOpen(false);
      fetchWorkspaceMembers(selectedWorkspaceForMembers);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setSavingMember(false);
    }
  };

  const handleInviteUser = async () => {
    if (!selectedWorkspaceForMembers) return;
    
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setSavingMember(true);
    try {
      await axios.post(
        `${API_URL}/admin/workspaces/${selectedWorkspaceForMembers.id}/invite`,
        {
          name: inviteForm.name,
          email: inviteForm.email,
          password: inviteForm.password,
          role: inviteForm.role
        }
      );
      
      toast.success('User invited successfully');
      setInviteDialogOpen(false);
      setInviteForm({ name: '', email: '', password: '', role: 'viewer' });
      fetchWorkspaceMembers(selectedWorkspaceForMembers);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSavingMember(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedWorkspaceForMembers) return;
    
    if (!window.confirm('Are you sure you want to remove this member from the workspace?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/admin/workspaces/${selectedWorkspaceForMembers.id}/members/${memberId}`
      );
      toast.success('Member removed from workspace');
      fetchWorkspaceMembers(selectedWorkspaceForMembers);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get(`${API_URL}/workspaces`);
      setWorkspaces(res.data);
      
      const stats = {};
      for (const ws of res.data) {
        try {
          const statsRes = await axios.get(`${API_URL}/admin/workspaces/${ws.id}/stats`);
          stats[ws.id] = statsRes.data;
        } catch (e) {
          stats[ws.id] = { documents: 0, members: 0 };
        }
      }
      setWorkspaceStats(stats);
    } catch (error) {
      toast.error('Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (workspaceId) => {
    try {
      const res = await axios.post(`${API_URL}/auth/switch-workspace/${workspaceId}`);
      localStorage.setItem('token', res.data.access_token);
      toast.success(`Switched to workspace: ${res.data.workspace.name}`);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to switch workspace');
    }
  };

  const handleEditWorkspace = (workspace) => {
    setSelectedWorkspace(workspace);
    const settings = workspace.settings || {};
    setEditForm({ 
      name: workspace.name, 
      subdomain: workspace.subdomain,
      logo_url: settings.logo_url || '',
      primary_color: settings.primary_color || '#7c3aed',
      secondary_color: settings.secondary_color || '#14b8a6',
      welcome_message: settings.welcome_message || '',
      contact_email: settings.contact_email || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API_URL}/admin/workspaces/${selectedWorkspace.id}`, {
        name: editForm.name,
        subdomain: editForm.subdomain
      });
      
      await axios.put(`${API_URL}/admin/workspaces/${selectedWorkspace.id}/settings`, {
        logo_url: editForm.logo_url,
        primary_color: editForm.primary_color,
        secondary_color: editForm.secondary_color,
        welcome_message: editForm.welcome_message,
        contact_email: editForm.contact_email
      });
      
      toast.success('Workspace updated successfully');
      setEditDialogOpen(false);
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workspace');
    }
  };

  const handleCreateWorkspace = async () => {
    if (!createForm.name || !createForm.subdomain || !createForm.admin_email || !createForm.admin_name || !createForm.admin_password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/workspaces`, createForm);
      toast.success('Workspace created successfully!');
      setCreateDialogOpen(false);
      setCreateForm({ name: '', subdomain: '', admin_email: '', admin_name: '', admin_password: '' });
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (workspace) => {
    setWorkspaceToDelete(workspace);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== workspaceToDelete.name) {
      toast.error('Please type the workspace name correctly to confirm deletion');
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/admin/workspaces/${workspaceToDelete.id}`);
      toast.success(`Workspace "${workspaceToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
      setDeleteConfirmText('');
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workspace');
    }
  };

  const filteredWorkspaces = workspaces.filter(ws =>
    ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ws.subdomain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user.is_super_admin) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Access Denied</h2>
            <p className="text-zinc-600">Only super admins can access this page</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="super-admin-dashboard">
        {/* Header with Branding */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {systemSettings.logo_url ? (
              <img src={systemSettings.logo_url} alt="Logo" className="h-12" />
            ) : (
              <div 
                className="h-12 w-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: systemSettings.primary_color }}
              >
                <Building2 className="h-6 w-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {systemSettings.app_name} - Admin Panel
              </h1>
              <p className="text-zinc-600">Manage all workspaces from one place</p>
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} style={{ backgroundColor: systemSettings.primary_color }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workspace
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="workspaces" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Workspaces
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Admin Settings
            </TabsTrigger>
          </TabsList>

          {/* Workspaces Tab */}
          <TabsContent value="workspaces">
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workspaces..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-zinc-200 rounded-lg p-4">
                <div className="text-2xl font-bold" style={{ color: systemSettings.primary_color }}>{workspaces.length}</div>
                <div className="text-sm text-zinc-600">Total Workspaces</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-4">
                <div className="text-2xl font-bold" style={{ color: systemSettings.primary_color }}>
                  {Object.values(workspaceStats).reduce((sum, s) => sum + (s.documents || 0), 0)}
                </div>
                <div className="text-sm text-zinc-600">Total Documents</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-4">
                <div className="text-2xl font-bold" style={{ color: systemSettings.primary_color }}>
                  {Object.values(workspaceStats).reduce((sum, s) => sum + (s.members || 0), 0)}
                </div>
                <div className="text-sm text-zinc-600">Total Members</div>
              </div>
            </div>

            {/* Workspaces Table */}
            {loading ? (
              <div className="text-center py-12 text-zinc-500">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                Loading workspaces...
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Workspace</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Subdomain</th>
                      <th className="text-center px-6 py-4 text-sm font-semibold text-zinc-900">Documents</th>
                      <th className="text-center px-6 py-4 text-sm font-semibold text-zinc-900">Members</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Created</th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-zinc-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorkspaces.map((ws) => (
                      <tr key={ws.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="h-10 w-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: ws.settings?.primary_color ? `${ws.settings.primary_color}20` : '#f4f4f5' }}
                            >
                              {ws.settings?.logo_url ? (
                                <img src={ws.settings.logo_url} alt={ws.name} className="h-8 w-8 rounded object-cover" />
                              ) : (
                                <Building2 className="h-5 w-5" style={{ color: ws.settings?.primary_color || '#7c3aed' }} />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-zinc-900">{ws.name}</div>
                              {ws.settings?.custom_domain && (
                                <div className="text-xs text-teal-600">{ws.settings.custom_domain}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm bg-zinc-100 px-2 py-1 rounded">{ws.subdomain}</code>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FileText className="h-4 w-4 text-zinc-400" />
                            <span>{workspaceStats[ws.id]?.documents || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="h-4 w-4 text-zinc-400" />
                            <span>{workspaceStats[ws.id]?.members || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          {new Date(ws.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleManageMembers(ws)} title="Manage Members">
                              <Users className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleEditWorkspace(ws)} title="Edit Workspace">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(`/portal/${ws.subdomain}`, '_blank')} title="View Portal">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleImpersonate(ws.id)} title="Switch to Workspace">
                              <LogIn className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteClick(ws)} title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* System Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">
                <Palette className="h-5 w-5 inline-block mr-2" />
                System-Wide Branding
              </h2>
              <p className="text-sm text-zinc-600 mb-6">Customize the appearance of the entire application</p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <Label>Application Name</Label>
                  <Input
                    value={systemSettings.app_name}
                    onChange={(e) => setSystemSettings({...systemSettings, app_name: e.target.value})}
                    placeholder="DocuBot"
                    className="mt-2"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Logo URL</Label>
                  <Input
                    value={systemSettings.logo_url}
                    onChange={(e) => setSystemSettings({...systemSettings, logo_url: e.target.value})}
                    placeholder="https://example.com/logo.png"
                    className="mt-2"
                  />
                  {systemSettings.logo_url && (
                    <div className="mt-3 p-4 bg-zinc-50 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-2">Preview:</p>
                      <img src={systemSettings.logo_url} alt="Logo preview" className="h-12 object-contain" />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={systemSettings.primary_color}
                      onChange={(e) => setSystemSettings({...systemSettings, primary_color: e.target.value})}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={systemSettings.primary_color}
                      onChange={(e) => setSystemSettings({...systemSettings, primary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={systemSettings.secondary_color}
                      onChange={(e) => setSystemSettings({...systemSettings, secondary_color: e.target.value})}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={systemSettings.secondary_color}
                      onChange={(e) => setSystemSettings({...systemSettings, secondary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Favicon URL</Label>
                  <Input
                    value={systemSettings.favicon_url}
                    onChange={(e) => setSystemSettings({...systemSettings, favicon_url: e.target.value})}
                    placeholder="https://example.com/favicon.ico"
                    className="mt-2"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Login Background Image URL</Label>
                  <Input
                    value={systemSettings.login_background_url}
                    onChange={(e) => setSystemSettings({...systemSettings, login_background_url: e.target.value})}
                    placeholder="https://example.com/background.jpg"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Theme</Label>
                  <select
                    value={systemSettings.theme}
                    onChange={(e) => setSystemSettings({...systemSettings, theme: e.target.value})}
                    className="w-full mt-2 px-3 py-2 border border-zinc-200 rounded-md"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>
              </div>

              <Button onClick={saveSystemSettings} disabled={savingSettings} className="mt-6">
                {savingSettings ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Admin Settings
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Workspace Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Workspace</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Workspace Name</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="My Company"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Subdomain</Label>
                <Input
                  value={createForm.subdomain}
                  onChange={(e) => setCreateForm({...createForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                  placeholder="my-company"
                  className="mt-2"
                />
                <p className="text-xs text-zinc-500 mt-1">Only lowercase letters, numbers, and hyphens</p>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-zinc-700 mb-3">Workspace Admin Account</p>
                <div className="space-y-3">
                  <div>
                    <Label>Admin Name</Label>
                    <Input
                      value={createForm.admin_name}
                      onChange={(e) => setCreateForm({...createForm, admin_name: e.target.value})}
                      placeholder="John Doe"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Admin Email</Label>
                    <Input
                      type="email"
                      value={createForm.admin_email}
                      onChange={(e) => setCreateForm({...createForm, admin_email: e.target.value})}
                      placeholder="admin@company.com"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Admin Password</Label>
                    <Input
                      type="password"
                      value={createForm.admin_password}
                      onChange={(e) => setCreateForm({...createForm, admin_password: e.target.value})}
                      placeholder="••••••••"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateWorkspace} disabled={creating}>
                {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Workspace
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Workspace</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Workspace Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Subdomain</Label>
                <Input
                  value={editForm.subdomain}
                  onChange={(e) => setEditForm({...editForm, subdomain: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input
                  value={editForm.logo_url}
                  onChange={(e) => setEditForm({...editForm, logo_url: e.target.value})}
                  placeholder="https://..."
                  className="mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={editForm.primary_color}
                      onChange={(e) => setEditForm({...editForm, primary_color: e.target.value})}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editForm.primary_color}
                      onChange={(e) => setEditForm({...editForm, primary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={editForm.secondary_color}
                      onChange={(e) => setEditForm({...editForm, secondary_color: e.target.value})}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editForm.secondary_color}
                      onChange={(e) => setEditForm({...editForm, secondary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Workspace</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-zinc-600 mb-4">
                This action cannot be undone. This will permanently delete the workspace 
                <span className="font-semibold"> "{workspaceToDelete?.name}"</span> and all its data.
              </p>
              <div>
                <Label>Type the workspace name to confirm:</Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={workspaceToDelete?.name}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText !== workspaceToDelete?.name}
              >
                Delete Workspace
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Member Management Dialog */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Manage Members - {selectedWorkspaceForMembers?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-zinc-600">
                  {workspaceMembers.length} member{workspaceMembers.length !== 1 ? 's' : ''}
                </p>
                <Button 
                  size="sm" 
                  onClick={() => setInviteDialogOpen(true)}
                  style={{ backgroundColor: systemSettings.primary_color }}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite User
                </Button>
              </div>
              
              {loadingMembers ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-zinc-400" />
                  <p className="text-sm text-zinc-500 mt-2">Loading members...</p>
                </div>
              ) : workspaceMembers.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-zinc-200 rounded-lg">
                  <Users className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
                  <p className="text-sm text-zinc-500">No members found</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-3"
                    onClick={() => setInviteDialogOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite First Member
                  </Button>
                </div>
              ) : (
                <div className="border border-zinc-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600">Member</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600">Role</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspaceMembers.map((member) => (
                        <tr key={member.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-zinc-500" />
                              </div>
                              <div>
                                <div className="font-medium text-sm text-zinc-900">{member.name}</div>
                                <div className="text-xs text-zinc-500">{member.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              member.role === 'workspace_admin' ? 'bg-purple-100 text-purple-700' :
                              member.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                              'bg-zinc-100 text-zinc-600'
                            }`}>
                              {member.role === 'workspace_admin' ? 'Admin' : 
                               member.role === 'editor' ? 'Editor' : 'Viewer'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleEditMember(member)}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600"
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog open={editMemberDialogOpen} onOpenChange={setEditMemberDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editMemberForm.name}
                  onChange={(e) => setEditMemberForm({...editMemberForm, name: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editMemberForm.email}
                  onChange={(e) => setEditMemberForm({...editMemberForm, email: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={editMemberForm.role}
                  onValueChange={(value) => setEditMemberForm({...editMemberForm, role: value})}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="workspace_admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Password (leave empty to keep current)</Label>
                <Input
                  type="password"
                  value={editMemberForm.password}
                  onChange={(e) => setEditMemberForm({...editMemberForm, password: e.target.value})}
                  placeholder="••••••••"
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditMemberDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveMember} disabled={savingMember}>
                {savingMember ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite User to {selectedWorkspaceForMembers?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                  placeholder="John Doe"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="john@example.com"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm({...inviteForm, role: value})}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="workspace_admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Password (required for new users)</Label>
                <Input
                  type="password"
                  value={inviteForm.password}
                  onChange={(e) => setInviteForm({...inviteForm, password: e.target.value})}
                  placeholder="••••••••"
                  className="mt-2"
                />
                <p className="text-xs text-zinc-500 mt-1">If user already exists, they will be added without a password</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleInviteUser} disabled={savingMember}>
                {savingMember ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Invite User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
